import { useAuthStore } from '@/store/auth';
import { useUIStore } from '@/app/model';
import React, { useEffect, useState } from 'react';
import {
  useLocation,
  useMatch,
  matchPath,
  useNavigate,
} from 'react-router-dom';
import { toolsLinks, type ToolId } from '@/shared/config/tools';
import { useClickOutside } from '@/hooks/ui/useClickOutside';
import { useUsers, useUserUtils } from '@/hooks/users/useUserActions';
import { WorkTimeCard } from '@/components/smart/workTimeCard';
import { WorkTimeTaskCardMobile } from '@/components/base/mobile/WorkTimeTaskCardMobile';
import { useActiveTimerTask } from '@/features/tasks/model/useActiveTimerTask';
import { CalendarSide } from '@/components/icons/calendarSide';
import { SideMenuLink } from './SideMenuLink';
import { Groups } from '@/components/icons/groups';
import { FileIcon } from '@/components/icons/file';
import { SettingsIconMobile } from '@/components/icons/settingsIconMobile';
import { GameChangerLogo } from '@/components/icons/gamechangerLogo';
import { ExitIcon } from '@/components/icons/exit';
import { UserAvatar } from '@/pages/groups/UserAvatar';
import {
  BarChart3,
  Bot,
  BookOpen,
  FileCode,
  FileSignature,
  Phone,
  ShoppingBag,
  ClipboardList,
  Smile,
  Truck,
  ChevronDown,
  Check,
} from 'lucide-react';
import { useDepartmentGroups } from '@/entities/department';
import styles from './SideMenu.module.css';
import clsx from 'clsx';

// Иконки для пунктов «инструментов» — в стиле остальных нав-ссылок (lucide).
const TOOL_ICONS: Record<
  ToolId,
  React.ComponentType<{ className?: string }>
> = {
  sales: ShoppingBag,
  eds: FileSignature,
  calls: Phone,
  bi: BarChart3,
  'knowledge-base': BookOpen,
  'time-report': ClipboardList,
  'ai-files': FileCode,
  'task-control': Bot,
  logistics: Truck,
};

// Пункт меню «инструмента» с явным маршрутом и иконкой.
type ToolMenuItem = {
  key: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
};

// Пункты инструментов для мобильного меню: все toolsLinks, причём
// «Контроль задач» (task-control) показываем как «AI Control».
// Плюс отдельный пункт «Эмоциональный модуль» с прямым маршрутом /emotion-module/.
const AI_CONTROL = toolsLinks.find((tool) => tool.id === 'task-control');
const TOOL_MENU_ITEMS: ToolMenuItem[] = [
  ...toolsLinks
    .filter((tool) => tool.id !== 'task-control')
    .map((tool) => ({
      key: tool.id,
      label: tool.label,
      path: `/tools/${tool.id}`,
      icon: TOOL_ICONS[tool.id],
    })),
  ...(AI_CONTROL
    ? [
        {
          key: AI_CONTROL.id,
          label: 'AI Control',
          path: `/tools/${AI_CONTROL.id}`,
          icon: TOOL_ICONS[AI_CONTROL.id],
        },
      ]
    : []),
  {
    key: 'emotion-module',
    label: 'Эмоциональный модуль',
    path: '/emotion-module/',
    icon: Smile,
  },
];

export const SideMenu: React.FC = () => {
  const { isMenuOpen, closeMenu, toggleMenu } = useUIStore();
  const { logout, userId } = useAuthStore();
  const menuRef = useClickOutside(closeMenu);
  const timer = useActiveTimerTask();
  const location = useLocation();
  const navigate = useNavigate();
  const homeMatch = useMatch({ path: '/', end: true });
  const projectsMatch = useMatch('/projects/');
  const settingsMatch = useMatch('/settings/');
  const [groupsOpen, setGroupsOpen] = useState(false);
  const { groups } = useDepartmentGroups();

  const selectedGroupId =
    matchPath('/groups/:id', location.pathname)?.params.id ?? null;

  // Получаем данные пользователя по ID
  const { data: user } = useUsers.useById(userId || 0);

  // Получаем данные пользователя
  const displayName = useUserUtils.getDisplayName(user);
  const position = user?.WorkPosition?.String || 'Пользователь'; // Исправил опечатку

  const handleLogout = () => {
    logout();
    closeMenu();
  };

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) {
        closeMenu();
      }
    };

    window.addEventListener('resize', onResize);
    onResize();

    return () => window.removeEventListener('resize', onResize);
  }, [closeMenu]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (
        data &&
        typeof data === 'object' &&
        data.source === 'checkerboard' &&
        data.type === 'toggle-sidebar'
      ) {
        toggleMenu();
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [toggleMenu]);

  return (
    <div
      className={clsx(styles.wrapper, isMenuOpen && styles.open)}
      style={{ pointerEvents: isMenuOpen ? 'auto' : 'none' }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          closeMenu();
        }
      }}
    >
      <div
        ref={menuRef}
        className={`absolute left-0 top-0 h-full w-80 bg-white shadow-xl transform transition-transform duration-300 ease-in-out font-roboto
    ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}
    overflow-y-auto overscroll-contain
  `}
      >
        <div className="h-full flex flex-col text-black">
          <div className="px-6 pt-[70px] pb-[30px]">
            <GameChangerLogo />
          </div>
          <div className="px-5">
            <div className="flex items-center gap-[5px]">
              <div className="w-[50px] h-[50px] rounded-full flex items-center justify-center">
                {user ? (
                  <UserAvatar user={user} size="xl" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-300 animate-pulse" />
                )}
              </div>
              <div className="gap-y-[3px]">
                <div className="font-medium leading-[130%] tracking-[-0.5px] text-[18px]">
                  {displayName}
                </div>
                <div className="text-[14px] font-normal leading-[130%] tracking-[-0.5px] text-[#6E6E6E]">
                  {position}
                </div>
              </div>
            </div>
          </div>
          <div className="p-5 mt-5">
            {timer.hasTask && (
              <WorkTimeTaskCardMobile
                title={timer.title}
                planned={timer.plannedMinutes}
                spent={timer.spentMinutes}
                isRunning={timer.isRunning!}
                isCompleted={timer.isCompleted}
                isRenewing={timer.isRenewing}
                onToggle={timer.toggleTask}
                onFinish={timer.finishTask}
                onRenew={timer.renew}
              />
            )}
            <WorkTimeCard userId={userId!} />

            <div className="mt-[20px]">
              <SideMenuLink
                to="/"
                isActive={!!homeMatch}
                onClick={closeMenu}
                icon={CalendarSide}
              >
                Мои задачи
              </SideMenuLink>

              <div>
                <button
                  type="button"
                  onClick={() => setGroupsOpen((v) => !v)}
                  className={`
                    flex items-center gap-3 w-full py-[10px] px-[15px] text-black
                    ${location.pathname.startsWith('/groups') ? 'bg-gray-100 rounded-[10px]' : ''}
                  `}
                >
                  <div className="w-[30px] h-[30px] flex items-center justify-center bg-[#E3E3E3] rounded-full">
                    <Groups className="text-black w-[18px] h-[18px] [stroke-width:1.5]" />
                  </div>
                  <span className="font-normal text-[18px] leading-[130%] tracking-[-0.5px] flex-1 text-left">
                    Отделы
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-black/60 transition-transform duration-200 ${groupsOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {groupsOpen && (
                  <div className="ml-[57px] pr-[15px] pb-1 flex flex-col">
                    {[...groups]
                      .sort((a, b) => a.Name.localeCompare(b.Name, 'ru'))
                      .map((group) => {
                        const isSelected = String(group.ID) === selectedGroupId;
                        return (
                          <button
                            key={group.ID}
                            type="button"
                            onClick={() => {
                              navigate(`/groups/${group.ID}`);
                              closeMenu();
                            }}
                            className={`
                              flex items-center justify-between py-[7px] text-left
                              text-[16px] leading-[130%] tracking-[-0.5px]
                              ${isSelected ? 'font-medium text-[#102653]' : 'font-normal text-black/80'}
                            `}
                          >
                            {group.Name}
                            {isSelected && (
                              <Check className="w-3.5 h-3.5 text-[#102653] shrink-0 ml-2" />
                            )}
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>

              <SideMenuLink
                to="/projects"
                isActive={!!projectsMatch}
                onClick={closeMenu}
                icon={FileIcon}
              >
                Проекты
              </SideMenuLink>
            </div>

            {/* Инструменты */}
            <div className="flex flex-col">
              {TOOL_MENU_ITEMS.map((item) => {
                const base = item.path.replace(/\/+$/, '');
                const isActive =
                  location.pathname === base ||
                  location.pathname.startsWith(`${base}/`);
                return (
                  <SideMenuLink
                    key={item.key}
                    to={item.path}
                    isActive={isActive}
                    onClick={closeMenu}
                    icon={item.icon}
                  >
                    {item.label}
                  </SideMenuLink>
                );
              })}
            </div>

            <div className="mt-[15px]">
              <SideMenuLink
                to="/settings"
                isActive={!!settingsMatch}
                onClick={closeMenu}
                icon={SettingsIconMobile}
              >
                Настройки
              </SideMenuLink>

              <button
                onClick={handleLogout}
                className="mt-[3px] flex items-center gap-3 w-full py-[10px] px-[15px] text-left transition-colors hover:bg-red-50"
              >
                <ExitIcon width={30} height={30} fill="#FC291A" />
                <span className="font-normal text-[18px] leading-[130%] tracking-[-0.5px] text-black">
                  Выйти
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
